import type { Voyage } from "../../shared/api/types";

// Per-column filter shape. Matches the Settings grid pattern:
//   - text columns: case-insensitive substring (`contains`)
//   - categorical columns: multi-select Set (empty = no constraint)
//   - date columns: independent from/to per column
export interface VesselsFilters {
  vesselCode: string;
  vesselName: string;
  voyageReference: string;
  voyageStatus: Set<string>;
  oprType: Set<string>;
  tradeArea: Set<string>;
  lob: Set<string>;
  commenceFrom: string;
  commenceTo: string;
  completeFrom: string;
  completeTo: string;
}

export type SortKey =
  | "vesselCode"
  | "vesselName"
  | "voyageReference"
  | "voyageStatus"
  | "oprType"
  | "tradeArea"
  | "lob"
  | "commenceDate"
  | "completeDate"
  | "totalSeaDays"
  | "totalPortDays"
  | "voyageNo";

export type SortDir = "asc" | "desc";

export interface SortEntry {
  key: SortKey;
  dir: SortDir;
}

export function emptyFilters(): VesselsFilters {
  return {
    vesselCode: "",
    vesselName: "",
    voyageReference: "",
    voyageStatus: new Set<string>(),
    oprType: new Set<string>(),
    tradeArea: new Set<string>(),
    lob: new Set<string>(),
    commenceFrom: "",
    commenceTo: "",
    completeFrom: "",
    completeTo: "",
  };
}

// Module-scope collator: constructing Intl collation per comparison is
// the slow path of localeCompare; .compare reuses one instance.
const numericCompare = new Intl.Collator(undefined, { numeric: true }).compare;

function containsFold(haystack: string | null | undefined, needleLower: string): boolean {
  if (!needleLower) return true;
  if (!haystack) return false;
  return haystack.toLowerCase().includes(needleLower);
}

function inRange(value: string | null | undefined, from: string, to: string): boolean {
  if (!from && !to) return true;
  if (!value) return false;
  // The filter UI emits YYYY-MM-DD, but voyage dates can be ISO
  // timestamps with a time component ("2026-05-15T14:30:00Z"). A naive
  // lexicographic compare against the date-only filter would treat
  // anything after midnight as > the filter day and silently drop the
  // row. Slice to YYYY-MM-DD so the comparison stays day-precise.
  const ymd = value.length > 10 ? value.slice(0, 10) : value;
  if (from && ymd < from) return false;
  if (to && ymd > to) return false;
  return true;
}

export function applyFilters(voyages: Voyage[], f: VesselsFilters): Voyage[] {
  const codeNeedle = f.vesselCode.toLowerCase();
  const nameNeedle = f.vesselName.toLowerCase();
  const refNeedle = f.voyageReference.toLowerCase();
  return voyages.filter((v) => {
    if (!containsFold(v.vesselCode, codeNeedle)) return false;
    if (!containsFold(v.vesselName, nameNeedle)) return false;
    if (!containsFold(v.voyageReference, refNeedle)) return false;
    if (f.voyageStatus.size > 0 && !f.voyageStatus.has(v.voyageStatus ?? "")) return false;
    if (f.oprType.size > 0 && !f.oprType.has(v.oprType ?? "")) return false;
    if (f.tradeArea.size > 0 && !f.tradeArea.has(v.tradeArea ?? "")) return false;
    if (f.lob.size > 0 && !f.lob.has(v.lob ?? "")) return false;
    if ((f.commenceFrom || f.commenceTo) && !inRange(v.commenceDate, f.commenceFrom, f.commenceTo)) return false;
    if ((f.completeFrom || f.completeTo) && !inRange(v.completeDate, f.completeFrom, f.completeTo)) return false;
    return true;
  });
}

// Single-key compare. Direction sign is applied at the call site so this
// stays a pure equality check. Mirrors `compareEventRow` in EventsSection.
function compareVoyage(a: Voyage, b: Voyage, key: SortKey): number {
  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  // Nulls sink to the bottom regardless of direction.
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === "number" && typeof bv === "number") return av - bv;
  return numericCompare(String(av), String(bv));
}

export function sortVoyages(voyages: Voyage[], order: SortEntry[]): Voyage[] {
  if (order.length === 0) return [...voyages];
  const out = [...voyages];
  out.sort((a, b) => {
    for (const e of order) {
      const dir = e.dir === "asc" ? 1 : -1;
      // Nulls always sink, regardless of dir — so flip the dir factor
      // back to natural when either side is null.
      const av = a[e.key]; const bv = b[e.key];
      if (av == null && bv == null) continue;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = compareVoyage(a, b, e.key) * dir;
      if (cmp !== 0) return cmp;
    }
    return 0;
  });
  return out;
}
